/**
 * When a constellation's reveal plays (quest-journey-2026-10-27; API v1 §1.2).
 *
 * The server says which is due (`nav.unlock`, one at a time) and which heroes
 * are owed (`nav.heroes_due`). A reveal plays over whatever screen the person
 * is on, at the natural end of an act: never mid-reading or mid-interview, and
 * a moment after the last thing settles. Earned while away, it plays on the
 * next app open. The Sun (`plays: quest_or_altar`) plays in the Quest (the
 * panel's own ceremony) or here after the Altar's first-visit ritual.
 *
 * Until it plays, the bar holds its old face (the new tab not yet born).
 */
import { ref, computed, watch, onUnmounted } from 'vue'
import { readReveals, heroDue, revealMayPlay, revealBar } from '@/utils/quest-reveals'
import { readJourney, findStar } from '@/utils/quest-sky'
import { getPlatformQuests, getMirrorProfile } from '@/api-service'
import { track } from '@/utils/analytics'

export const SETTLE_MS = 1200
const GATHER_MS = 6000

const withTimeout = (p, ms) => Promise.race([p, new Promise((resolve) => setTimeout(() => resolve(null), ms))])
const wordOf = (j, key) => { const h = findStar(j, key); return h && h.star.done ? h.star.word : '' }

/**
 * nav: computed raw `nav`; navModel: readNav's model; ctx: computed
 * { signedIn, inReading, interviewing, introRoom, busy }; panel: computed
 * activePanel; markSeen(key); store: the app store.
 */
export function useQuestReveals ({ nav, navModel, ctx, panel, markSeen, store }) {
  const playing = ref(null)
  const phase = ref('')
  const data = ref({})
  const handled = ref([])
  const altarRitualDone = ref(false)
  let timer = null

  const due = computed(() => readReveals(nav.value).filter((r) => !handled.value.includes(r.seen)))
  const mayPlay = () => revealMayPlay(ctx.value || {})

  /** The next thing to show: the due reveal, else a hero still owed. */
  const upcoming = computed(() => {
    const r = due.value[0]
    if (r) {
      if (r.plays === 'quest_or_altar') return panel.value === 'altar' && altarRitualDone.value ? r : null
      return r
    }
    const h = heroDue(nav.value)
    // The Altar's hero is its own rite: never shown here.
    if (h && h.key === 'mo') return null
    return h && !handled.value.includes(h.hero) ? h : null
  })

  /** The bar while a reveal waits or plays: the old face until the light lands. */
  function bar (b) {
    if (!b || !Array.isArray(b.items)) return b
    const r = playing.value || due.value.find((x) => x.plays !== 'quest_or_altar') || null
    if (!r || r.heroOnly) return b
    const ph = playing.value ? (phase.value || 'before') : 'before'
    return { ...b, items: revealBar(b.items, r, ph, { mystic: navModel.value && navModel.value.mystic }) }
  }

  /** What the art and the hero show, from the sky and the Mirror. */
  function baseData () {
    const out = { mystic: navModel.value && navModel.value.mystic ? { ...navModel.value.mystic } : null }
    const gift = (navModel.value && navModel.value.skyGift) || null
    out.name = (gift && gift.name) || store.querent_name || ''
    out.portrait = store.querent_avatar_url || (gift && gift.portrait) || ''
    return out
  }
  async function gather (r) {
    const sid = store.session_id
    const out = baseData()
    if (!sid) return out
    const [q, m] = await Promise.all([
      getPlatformQuests(sid).catch(() => null),
      ['ri', 'zw'].includes(r.key) ? getMirrorProfile(sid).catch(() => null) : Promise.resolve(null),
    ])
    const j = q && q.sky ? readJourney(q.sky) : null
    if (m && m.signs) {
      out.signs = { sun: m.signs.sun || '', moon: m.signs.moon || '', rising: m.signs.rising || '' }
      out.sunSign = out.signs.sun
    }
    if (j) {
      if (!out.sunSign) out.sunSign = j.sunSign
      const circle = wordOf(j, 've_circle'); const readFor = wordOf(j, 've_read')
      out.other = readFor || circle
      out.people = [...new Set([readFor, circle].filter(Boolean))]
      if (navModel.value && navModel.value.door === 'gift' && circle) out.from = circle
      out.lesson_from = wordOf(j, 'zw_lesson')
      // The Studio's price as a goal (API §3 sky.studio).
      if (q.sky.studio && q.sky.studio.price) out.studio = { price: q.sky.studio.price, balance: q.sky.studio.balance || 0, enough: !!q.sky.studio.enough }
      const fs = findStar(j, 'fs_read')
      if (fs && fs.star.moment) out.day = String(fs.star.moment.at || '').slice(0, 10)
    }
    return out
  }

  // What the art shows is fetched as soon as a reveal is due, so it is ready
  // when the moment comes (the sky can be slow on a cold server).
  let pre = { key: '', p: null }
  function prefetch (r) {
    const k = r ? `${r.key}:${r.heroOnly ? 'h' : 'r'}` : ''
    if (!r || pre.key === k) return pre.p
    pre = { key: k, p: gather(r).catch(() => ({})) }
    return pre.p
  }
  watch(upcoming, (r) => { if (r) prefetch(r) }, { immediate: true })

  function schedule () {
    if (playing.value || timer || !upcoming.value || !mayPlay()) return
    timer = setTimeout(async () => {
      timer = null
      const r = upcoming.value
      if (!r || playing.value || !mayPlay()) return
      const pr = prefetch(r)
      const d = await withTimeout(pr, GATHER_MS)
      if (playing.value || !mayPlay() || upcoming.value !== r) { schedule(); return }
      data.value = d || baseData()
      // Slow sky: the words fill in when they come, even mid-animation.
      if (!d) pr.then((x) => { if (playing.value && playing.value.key === r.key && x) data.value = { ...data.value, ...x } })
      phase.value = ''
      playing.value = { ...r }
    }, SETTLE_MS)
  }
  watch([upcoming, () => mayPlay()], () => {
    if (!mayPlay() && timer) { clearTimeout(timer); timer = null }
    schedule()
  }, { immediate: true })

  function onPhase (p) {
    phase.value = p
    if (p === 'arrived' && playing.value) track('nav_unlock_seen', { key: playing.value.apiKey })
  }
  function onSeen (key) {
    if (!handled.value.includes(key)) handled.value = [...handled.value, key]
    markSeen(key)
  }
  function finish () {
    const r = playing.value
    if (!r) return null
    // The light may not have landed (closed early): the reveal is seen all the same.
    const post = [r.heroOnly ? null : r.seen, r.hero].filter((k) => k && !handled.value.includes(k))
    handled.value = [...handled.value, ...post]
    for (const k of post) markSeen(k)
    playing.value = null
    phase.value = ''
    return r
  }

  function onAltarRitualDone () { altarRitualDone.value = true; schedule() }
  if (typeof window !== 'undefined') window.addEventListener('altar-ritual-done', onAltarRitualDone)
  onUnmounted(() => {
    if (timer) clearTimeout(timer)
    if (typeof window !== 'undefined') window.removeEventListener('altar-ritual-done', onAltarRitualDone)
  })

  // Money appears for the first time with the Zodiac Wheel's welcome: until
  // its reveal has played, the bar shows no carats.
  const holdsMoney = computed(() => {
    const r = playing.value || due.value[0]
    return !!(r && r.key === 'zw')
  })

  return { holdsMoney, revealPlaying: playing, revealData: data, revealPhase: phase, revealBarFor: bar, onRevealPhase: onPhase, onRevealSeen: onSeen, finishReveal: finish }
}
